import { useState, useEffect } from 'react';
import { LogOut, Plus, ArrowRight, Archive } from 'lucide-react';
import { Clock } from './components/Clock';
import { DashboardStats } from './components/DashboardStats';
import { RoomCard } from './components/RoomCard';
import { LatePaymentsByBuilding } from './components/LatePaymentsByBuilding';
import { LoginPage } from './components/LoginPage';
import { EditResidentModal } from './components/EditResidentModal';
import { EditMotorcyclePlateModal } from './components/EditMotorcyclePlateModal';
import { MoveResidentModal } from './components/MoveResidentModal';
import { PaymentDiscrepancyChecker } from './components/PaymentDiscrepancyChecker';
import { NotificationBell } from './components/NotificationBell';
import { useSupabaseRooms } from './hooks/useSupabaseRooms';
import { scrollToRoom } from './utils/scroll';
import { findResidentsWithDiscrepancies } from './utils/paymentChecker';
import type { User } from './types/auth';
import type { Resident } from './types';

import { DarkModeToggle } from './components/DarkModeToggle';
import { SearchBar } from './components/SearchBar';
import { CheckPaymentsButton } from './components/CheckPaymentsButton';
import { UnifiedTransactionModal } from './components/UnifiedTransactionModal';
import { AdminPanel } from './components/AdminPanel';
import { ManualBackupsHistory } from './components/ManualBackupsHistory';

function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    // Initialize from localStorage if available with error handling
    try {
      const savedUser = localStorage.getItem('currentUser');
      if (savedUser) {
        const parsedUser = JSON.parse(savedUser);
        // Validate the user object structure
        if (parsedUser && parsedUser.id && parsedUser.username && parsedUser.accessLevel) {
          return parsedUser;
        }
      }
    } catch (error) {
      console.error('Error parsing saved user:', error);
      localStorage.removeItem('currentUser');
    }
    return null;
  });

  const [searchResults, setSearchResults] = useState<{ name: string; roomNumber: string }[]>([]);
  const [showEditResidentModal, setShowEditResidentModal] = useState(false);
  const [showMotorcyclePlateModal, setShowMotorcyclePlateModal] = useState(false);
  const [showMoveResidentModal, setShowMoveResidentModal] = useState(false);
  const [showPaymentDiscrepancies, setShowPaymentDiscrepancies] = useState(false);
  const [editingResident, setEditingResident] = useState<Resident | null>(null);
  const [showUnifiedModal, setShowUnifiedModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showManualBackups, setShowManualBackups] = useState(false);

  // Use Supabase hooks instead of local state
  const {
    rooms,
    loading,
    error,
    addResident,
    addPaymentPeriod,
  updatePaymentPeriod,
    updateResident,
    moveResident,
    deletePaymentPeriod,
    deleteResident,
    backupResident,
    toggleRoomExpansion,
    getLatePayments,
    getResidentsWithoutPaymentProof,
    refreshData
  } = useSupabaseRooms(currentUser ?? undefined);

  // Separate rooms by building type
  const gajah4Rooms = rooms.filter(room => room.buildingType === 'gajah4' || !room.buildingType);
  const gajah3Rooms = rooms.filter(room => room.buildingType === 'gajah3');
  const anduringRooms = rooms.filter(room => room.buildingType === 'anduring');
  const limauManisRooms = rooms.filter(room => room.buildingType === 'limau_manis');
  
  // Check if user is admin (yudha or irfan) to show gajah3 section
  const isAdmin = currentUser && (currentUser.username === 'yudha' || currentUser.username === 'irfan' || currentUser.username === 'oeoel');
  
  // Check if user is resident - they should only see their own room
  const isResident = currentUser?.role === 'resident';
  
  // Note: Room filtering is now handled in useSupabaseRooms hook
  // No additional filtering needed here as data is already filtered by user permissions

  // Save user to localStorage whenever currentUser changes
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent).detail as { resident: Resident; period: any; updates: any };
      if (!detail) return;
      try {
        await updatePaymentPeriod(detail.resident, detail.period, detail.updates);
      } catch (err) {
        console.error('Failed to update payment period:', err);
      }
    };
    window.addEventListener('edit-payment-save', handler as EventListener);
    return () => window.removeEventListener('edit-payment-save', handler as EventListener);
  }, [updatePaymentPeriod]);

  useEffect(() => {
    try {
      if (currentUser) {
        localStorage.setItem('currentUser', JSON.stringify(currentUser));
      } else {
        localStorage.removeItem('currentUser');
      }
    } catch (error) {
      console.error('Error saving user to localStorage:', error);
    }
  }, [currentUser]);

  // Prevent automatic logout by checking localStorage periodically
  useEffect(() => {
    const checkAuthStatus = () => {
      try {
        const savedUser = localStorage.getItem('currentUser');
        if (savedUser && !currentUser) {
          const parsedUser = JSON.parse(savedUser);
          if (parsedUser && parsedUser.id && parsedUser.username && parsedUser.accessLevel) {
            setCurrentUser(parsedUser);
          }
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
      }
    };

    // Check auth status every 5 seconds
    const interval = setInterval(checkAuthStatus, 5000);
    
    return () => clearInterval(interval);
  }, [currentUser]);

  const handleRoomClick = (roomNumber: string) => {
    const element = document.getElementById(`room-${roomNumber}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add a temporary highlight effect
      element.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.5)';
      setTimeout(() => {
        element.style.boxShadow = '';
      }, 2000);
    }
  };

  const handleSearch = (query: string) => {
    if (!query) {
      setSearchResults([]);
      return;
    }

    const results = rooms.flatMap(room =>
      room.residents
        .filter(resident => 
          resident.name.toLowerCase().includes(query.toLowerCase()) || 
          resident.phone.includes(query) ||
          (resident.motorcyclePlate && resident.motorcyclePlate.toLowerCase().includes(query.toLowerCase()))
        )
        .map(resident => ({ name: resident.name, roomNumber: room.number }))
    );

    setSearchResults(results);
  };

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
  };

  const handleEditResident = (resident: Resident) => {
    setEditingResident(resident);
    // If user is resident, show motorcycle plate modal only
    // Otherwise show full edit modal
    if (currentUser?.role === 'resident') {
      setShowMotorcyclePlateModal(true);
    } else {
      setShowEditResidentModal(true);
    }
  };

  const handleBackupResident = async (residentId: string, reason?: string) => {
    const result = await backupResident(residentId, reason);
    if (!result.success) {
      throw new Error(result.error || 'Failed to backup resident');
    }
  };

  const handleAddPayment = async (
    roomNumber: string,
    residentPosition: 1 | 2,
    periodData: {
      periodNumber: number;
      paymentProofDate?: string;
      paymentProofLink?: string;
      nextPaymentDate?: string;
      category?: 'DP' | 'Pelunasan' | 'Cicilan';
      installmentNumber?: number;
    }
  ) => {
    const success = await addPaymentPeriod(roomNumber, residentPosition, periodData);
    if (success) {
      // unified modal handles close
    }
    return success;
  };

  const handleAddResidentData = async (
    roomNumber: string,
    position: 1 | 2,
    residentData: {
      name: string;
      phone: string;
      motorcyclePlate?: string;
      study: string;
      startDate: string;
      paymentPattern: number;
      paymentProofDate?: string;
      paymentProofLink?: string;
      nextPaymentDate?: string;
      category?: 'DP' | 'Pelunasan' | 'Cicilan';
      installmentNumber?: number;
    }
  ) => {
    const success = await addResident(roomNumber, position, residentData);
    if (success) {
      // unified modal handles close
    }
    return success;
  };

  const handleUpdateResident = async (residentId: string, updates: Partial<Resident>) => {
    const success = await updateResident(residentId, updates);
    if (success) {
      setShowEditResidentModal(false);
      setEditingResident(null);
    }
    return success;
  };

  const handleUpdateResidentForDiscrepancy = async (residentId: string, updates: Partial<Resident>) => {
    return await updateResident(residentId, updates);
  };

  const handleMoveResident = async (
    fromRoomNumber: string,
    fromPosition: 1 | 2,
    toRoomNumber: string,
    toPosition: 1 | 2
  ) => {
    const success = await moveResident(fromRoomNumber, fromPosition, toRoomNumber, toPosition);
    if (success) {
      setShowMoveResidentModal(false);
    }
    return success;
  };

  if (!currentUser) {
    return <LoginPage onLogin={setCurrentUser} />;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-light-background dark:bg-dark-background flex items-center justify-center">
        <div className="text-center card p-10">
          <img 
            src="/logokos.png" 
            alt="deKost SRI 2 Logo" 
            className="h-28 w-28 mx-auto mb-6"
          />
          <p className="text-xl text-light-textSecondary dark:text-dark-textSecondary font-semibold">Memuat data dari database...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-light-background dark:bg-dark-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto card p-8">
          <div className="text-error-600 mb-6">
            <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-light-text dark:text-dark-text mb-4">Error Loading Data</h2>
          <p className="text-lg text-light-textSecondary dark:text-dark-textSecondary mb-6">{error}</p>
          <button
            onClick={refreshData}
            className="btn-primary w-full"
          >
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }
  const residentsWithoutProof = getResidentsWithoutPaymentProof();
  
  // Note: Room filtering is handled in useSupabaseRooms hook based on user permissions
  // Using rooms directly as they are already filtered by user access level
  
  // Check if user is admin or manager
  const isAdminOrManager = currentUser && ['admin', 'manager'].includes(currentUser.role || '') || currentUser?.accessLevel === 1;
  
  return (
    <div className="min-h-screen transition-colors duration-500 relative overflow-hidden bg-gray-50 dark:bg-dark-background">
      {/* Clean solid background - no blur effects */}
      <div className="fixed inset-0 bg-gradient-to-br from-light-background to-white dark:from-dark-background dark:to-slate-900 -z-10"></div>
      
      <DarkModeToggle />
      
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6 relative z-10">
        {/* Clean Header - Solid Colors */}
        <div className="card p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <div className="flex flex-col gap-4">
                {/* Logo & Title */}
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 sm:w-14 sm:h-14 bg-primary-600 rounded-xl flex items-center justify-center border-4 border-primary-700">
                    <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-lg sm:text-2xl font-bold text-light-text dark:text-dark-text">
                      {isResident ? `Dashboard Kamar ${currentUser.roomNumber}` : 'Dashboard Kos Gajah 4'}
                    </h1>
                    <p className="text-sm sm:text-base text-light-textSecondary dark:text-dark-textSecondary">
                      Selamat datang, <span className="font-bold text-primary-600 dark:text-primary-400">{currentUser.username}</span>
                    </p>
                  </div>
                </div>
              
                {/* Action Buttons - Mobile */}
                <div className="flex flex-wrap gap-2 sm:hidden">
                  {!isResident && (
                    <>
                      <button
                        onClick={() => setShowUnifiedModal(true)}
                        className="flex items-center gap-1 px-3 py-2 btn-primary text-sm"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Transaksi</span>
                      </button>
                      <button
                        onClick={() => setShowMoveResidentModal(true)}
                        className="flex items-center gap-1 px-3 py-2 btn-secondary text-sm"
                      >
                        <ArrowRight className="h-4 w-4" />
                        <span>Pindah</span>
                      </button>
                    </>
                  )}
                  {isResident && (
                    <button
                      onClick={() => setShowUnifiedModal(true)}
                      className="flex items-center gap-1 px-3 py-2 btn-accent text-sm"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Bayar Kos</span>
                    </button>
                  )}
                  {currentUser && currentUser.accessLevel === 1 && (
                    <CheckPaymentsButton
                      onClick={() => setShowPaymentDiscrepancies(!showPaymentDiscrepancies)}
                      discrepancies={findResidentsWithDiscrepancies(rooms).length}
                    />
                  )}
                  {currentUser && (currentUser.username === 'irfan' || currentUser.username === 'yudha' || currentUser.username === 'oeoel') && (
                    <>
                      <button
                        onClick={() => setShowManualBackups(true)}
                        className="flex items-center gap-1 px-3 py-2 btn-secondary text-sm"
                      >
                        <Archive className="h-4 w-4" />
                        <span>Backup</span>
                      </button>
                      <button
                        onClick={() => setShowAdminPanel(true)}
                        className="flex items-center gap-1 px-3 py-2 btn-primary text-sm"
                      >
                        <span>{currentUser.username === 'yudha' || currentUser.username === 'oeoel' ? 'Akun' : 'Admin'}</span>
                      </button>
                    </>
                  )}
                  {currentUser && (
                    <NotificationBell 
                      userId={currentUser.id} 
                      username={currentUser.username}
                    />
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-1 px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-sm"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              
                {/* Action Buttons - Desktop */}
                <div className="hidden sm:flex items-center gap-2 flex-wrap">
                  {!isResident && (
                    <>
                      <button
                        onClick={() => setShowUnifiedModal(true)}
                        className="flex items-center gap-2 px-4 py-2 btn-primary text-sm"
                        title="Transaksi (Penghuni Lama/Baru)"
                      >
                        <Plus className="h-4 w-4" />
                        Transaksi
                      </button>
                      <button
                        onClick={() => setShowMoveResidentModal(true)}
                        className="flex items-center gap-2 px-4 py-2 btn-secondary text-sm"
                        title="Pindah Penghuni"
                      >
                        <ArrowRight className="h-4 w-4" />
                        Pindah Penghuni
                      </button>
                    </>
                  )}
                  {isResident && (
                    <button
                      onClick={() => setShowUnifiedModal(true)}
                      className="flex items-center gap-2 px-4 py-2 btn-accent text-sm"
                      title="Input Pembayaran Kos"
                    >
                      <Plus className="h-4 w-4" />
                      Input Pembayaran Kos
                    </button>
                  )}
                  {currentUser && currentUser.accessLevel === 1 && (
                    <CheckPaymentsButton
                      onClick={() => setShowPaymentDiscrepancies(!showPaymentDiscrepancies)}
                      discrepancies={findResidentsWithDiscrepancies(rooms).length}
                    />
                  )}
                  {currentUser && (currentUser.username === 'irfan' || currentUser.username === 'yudha' || currentUser.username === 'oeoel') && (
                    <>
                      <button
                        onClick={() => setShowManualBackups(true)}
                        className="flex items-center gap-2 px-4 py-2 btn-secondary text-sm"
                        title="Riwayat Backup Manual"
                      >
                        <Archive className="h-4 w-4" />
                        Backup Manual
                      </button>
                      <button
                        onClick={() => setShowAdminPanel(true)}
                        className="flex items-center gap-2 px-4 py-2 btn-primary text-sm"
                        title="Admin Panel"
                      >
                        {currentUser.username === 'yudha' || currentUser.username === 'oeoel' ? 'Akun Penghuni' : 'Admin Panel'}
                      </button>
                    </>
                  )}
                  {currentUser && (
                    <NotificationBell 
                      userId={currentUser.id} 
                      username={currentUser.username}
                    />
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-5 py-3 text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/30 rounded-xl transition-colors text-base font-semibold border-2 border-error-200 dark:border-error-700"
                    title="Logout"
                  >
                    <LogOut className="h-5 w-5" />
                    Keluar
                  </button>
                </div>
              </div>
            </div>
            
            {/* Clock - Desktop Only */}
            <div className="hidden md:block ml-6">
              <Clock />
            </div>
          </div>
        </div>

        {/* Clock - Mobile */}
        <div className="md:hidden mb-4 ">
          <Clock />
        </div>

        {/* Hide search bar for residents */}
        {!isResident && <SearchBar onSearch={handleSearch} />}
        
        {/* Hide search results for residents */}
        {!isResident && searchResults.length > 0 && (
          <div className="mt-4 card p-5">
            <h2 className="text-xl font-bold mb-4 text-light-text dark:text-dark-text">Hasil Pencarian</h2>
            <ul className="space-y-3">{searchResults.map((result, index) => (
                <li
                  key={index}
                  className="py-3 px-4 rounded-lg bg-primary-50 dark:bg-primary-900/40 border-2 border-primary-200 dark:border-primary-700 text-base text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800/50 cursor-pointer transition-colors font-semibold"
                  onClick={() => {
                    scrollToRoom(result.roomNumber);
                    toggleRoomExpansion(result.roomNumber);
                  }}
                >
                  {result.name} - Kamar No {result.roomNumber}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Dashboard Stats and Late Payments - Only for non-residents */}
        {!isResident && (
          <>
            <DashboardStats 
              rooms={gajah4Rooms}
              latePaymentsCount={getLatePayments().filter(payment => 
                gajah4Rooms.some(room => room.number === payment.roomNumber)
              ).length}
              residentsWithoutProof={residentsWithoutProof.filter(resident =>
                gajah4Rooms.some(room => room.number === resident.roomNumber)
              )}
              currentUser={currentUser}
              onRoomClick={handleRoomClick}
            />
            
            {/* Daftar Pembayaran Tertunggak - Gajah 4 */}
            <LatePaymentsByBuilding 
              rooms={gajah4Rooms}
            />
          </>
        )}
        
        {/* Transaction button for residents */}
        {isResident && (
          <div className="mb-6">
            <button
              onClick={() => setShowUnifiedModal(true)}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 btn-accent text-lg"
            >
              <Plus className="h-5 w-5" />
              Input Pembayaran Kos
            </button>
          </div>
        )}
        
        {/* Payment Discrepancy Checker - Only for admin */}
        {currentUser && currentUser.accessLevel === 1 && showPaymentDiscrepancies && (
          <PaymentDiscrepancyChecker
            rooms={rooms}
            onUpdateResident={handleUpdateResidentForDiscrepancy}
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
          {gajah4Rooms.map(room => (
            <div key={room.number} id={`room-${room.number}`}>
              <RoomCard
                room={room}
                currentUser={currentUser}
                onToggleExpand={() => toggleRoomExpansion(room.number)}
                onDeleteRoom={() => {}} // Not implemented for safety
                onPaymentPatternChange={() => {}} // Handled through edit resident modal
                onPaymentPeriodChange={() => {}} // Not needed with Supabase
                onAddPaymentPeriod={() => {}} // Handled through unified modal
                onResidentInfoChange={() => {}} // Handled through edit resident modal
                onDeletePaymentPeriod={deletePaymentPeriod}
                onDeleteResident={deleteResident}
                onEditResident={handleEditResident}
                onBackupResident={handleBackupResident}
              />
            </div>
          ))}
        </div>

        {/* Gajah 3 Section - Only visible for admin users */}
        {isAdmin && gajah3Rooms.length > 0 && (
          <>
            <div className="mt-12 mb-8">
              <div className="section-divider"></div>
              <h2 className="text-2xl sm:text-3xl font-bold text-light-text dark:text-dark-text text-center">
                Dashboard Pembayaran Kos Gajah 3
              </h2>
            </div>

            <DashboardStats 
              rooms={gajah3Rooms}
              latePaymentsCount={getLatePayments().filter(payment => 
                gajah3Rooms.some(room => room.number === payment.roomNumber)
              ).length}
              residentsWithoutProof={residentsWithoutProof.filter(resident =>
                gajah3Rooms.some(room => room.number === resident.roomNumber)
              )}
              currentUser={currentUser}
              onRoomClick={handleRoomClick}
            />

            {/* Daftar Pembayaran Tertunggak - Gajah 3 */}
            <LatePaymentsByBuilding 
              rooms={gajah3Rooms}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
              {gajah3Rooms.map(room => (
                <div key={room.number} id={`room-${room.number}`}>
                  <RoomCard
                    room={room}
                    currentUser={currentUser}
                    onToggleExpand={() => toggleRoomExpansion(room.number)}
                    onDeleteRoom={() => {}} // Not implemented for safety
                    onPaymentPatternChange={() => {}} // Handled through edit resident modal
                    onPaymentPeriodChange={() => {}} // Not needed with Supabase
                    onAddPaymentPeriod={() => {}} // Handled through unified modal
                    onResidentInfoChange={() => {}} // Handled through edit resident modal
                    onDeletePaymentPeriod={deletePaymentPeriod}
                    onDeleteResident={deleteResident}
                    onEditResident={handleEditResident}
                    onBackupResident={handleBackupResident}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Anduring Section - Only visible for admin users */}
        {isAdmin && anduringRooms.length > 0 && (
          <>
            <div className="mt-12 mb-8">
              <div className="section-divider"></div>
              <h2 className="text-2xl sm:text-3xl font-bold text-light-text dark:text-dark-text text-center">
                Dashboard Pembayaran Kos Anduring
              </h2>
            </div>

            <DashboardStats 
              rooms={anduringRooms}
              latePaymentsCount={getLatePayments().filter(payment => 
                anduringRooms.some(room => room.number === payment.roomNumber)
              ).length}
              residentsWithoutProof={residentsWithoutProof.filter(resident =>
                anduringRooms.some(room => room.number === resident.roomNumber)
              )}
              currentUser={currentUser}
              onRoomClick={handleRoomClick}
            />

            {/* Daftar Pembayaran Tertunggak - Anduring */}
            <LatePaymentsByBuilding 
              rooms={anduringRooms}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
              {anduringRooms.map(room => (
                <div key={room.number} id={`room-${room.number}`}>
                  <RoomCard
                    room={room}
                    currentUser={currentUser}
                    onToggleExpand={() => toggleRoomExpansion(room.number)}
                    onDeleteRoom={() => {}} // Not implemented for safety
                    onPaymentPatternChange={() => {}} // Handled through edit resident modal
                    onPaymentPeriodChange={() => {}} // Not needed with Supabase
                    onAddPaymentPeriod={() => {}} // Handled through unified modal
                    onResidentInfoChange={() => {}} // Handled through edit resident modal
                    onDeletePaymentPeriod={deletePaymentPeriod}
                    onDeleteResident={deleteResident}
                    onEditResident={handleEditResident}
                    onBackupResident={handleBackupResident}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Limau Manis Section - Only visible for admin users */}
        {isAdmin && limauManisRooms.length > 0 && (
          <>
            <div className="mt-12 mb-8">
              <div className="section-divider"></div>
              <h2 className="text-2xl sm:text-3xl font-bold text-light-text dark:text-dark-text text-center">
                Dashboard Pembayaran Kos Limau Manis (Kos Cowo)
              </h2>
            </div>

            <DashboardStats 
              rooms={limauManisRooms}
              latePaymentsCount={getLatePayments().filter(payment => 
                limauManisRooms.some(room => room.number === payment.roomNumber)
              ).length}
              residentsWithoutProof={residentsWithoutProof.filter(resident =>
                limauManisRooms.some(room => room.number === resident.roomNumber)
              )}
              currentUser={currentUser}
              onRoomClick={handleRoomClick}
            />

            {/* Daftar Pembayaran Tertunggak - Limau Manis */}
            <LatePaymentsByBuilding 
              rooms={limauManisRooms}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
              {limauManisRooms.map(room => (
                <div key={room.number} id={`room-${room.number}`}>
                  <RoomCard
                    room={room}
                    currentUser={currentUser}
                    onToggleExpand={() => toggleRoomExpansion(room.number)}
                    onDeleteRoom={() => {}} // Not implemented for safety
                    onPaymentPatternChange={() => {}} // Handled through edit resident modal
                    onPaymentPeriodChange={() => {}} // Not needed with Supabase
                    onAddPaymentPeriod={() => {}} // Handled through unified modal
                    onResidentInfoChange={() => {}} // Handled through edit resident modal
                    onDeletePaymentPeriod={deletePaymentPeriod}
                    onDeleteResident={deleteResident}
                    onEditResident={handleEditResident}
                    onBackupResident={handleBackupResident}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Unified transaction modal */}
        <UnifiedTransactionModal
          isOpen={showUnifiedModal}
          onClose={() => setShowUnifiedModal(false)}
          rooms={rooms}
          currentUser={currentUser}
          onAddPayment={handleAddPayment}
          onAddResident={handleAddResidentData}
        />

        {/* Modal edit lengkap untuk admin, manager, dan user */}
        {currentUser && currentUser.role !== 'resident' && (
          <EditResidentModal
            isOpen={showEditResidentModal}
            onClose={() => {
              setShowEditResidentModal(false);
              setEditingResident(null);
            }}
            resident={editingResident}
            onUpdateResident={handleUpdateResident}
          />
        )}

        {/* Modal khusus untuk resident - hanya bisa edit plat motor */}
        {currentUser?.role === 'resident' && (
          <EditMotorcyclePlateModal
            isOpen={showMotorcyclePlateModal}
            onClose={() => {
              setShowMotorcyclePlateModal(false);
              setEditingResident(null);
            }}
            resident={editingResident}
            onUpdateResident={handleUpdateResident}
          />
        )}

        {isAdminOrManager && (
          <MoveResidentModal
            isOpen={showMoveResidentModal}
            onClose={() => setShowMoveResidentModal(false)}
            rooms={rooms}
            onMoveResident={handleMoveResident}
          />
        )}

        {currentUser && (currentUser.username === 'irfan' || currentUser.username === 'yudha' || currentUser.username === 'oeoel') && (
          <ManualBackupsHistory
            visible={showManualBackups}
            onClose={() => setShowManualBackups(false)}
          />
        )}

        <AdminPanel 
          visible={showAdminPanel} 
          onClose={() => setShowAdminPanel(false)} 
          currentUser={currentUser} 
        />
      </div>
      
    </div>
  );
}

export default App;
